import type { HttpErrorResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

import { inject } from '@angular/core';
import Bugsnag from '@bugsnag/js';
import { environment } from '@environment';
import { AbTestCampaignNameEnum, abTestDefaultVariantMapping } from '@models/ab-test-models';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { initABTestCampaigns, initABTestCampaignsSuccess } from '@stores/abtest/abtest-ngrx.action';
import { userId } from '@wizbii-utils/angular/stores';
import { AbTestWebservice } from '@wizbii-utils/angular/webservices';
import { UserTypeEnum } from '@wizbii/utils/models';
import { catchError, map, of, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';

export const initAbTest = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => initABTestCampaigns())
    ),
  { functional: true }
);

export const initABTestCampaignsEffect = createEffect(
  (actions$ = inject(Actions), abTestWebservice = inject(AbTestWebservice), store = inject(Store)) =>
    actions$.pipe(
      ofType(initABTestCampaigns),
      switchMap(() => store.select(userId)),
      filter((userId) => !!userId),
      switchMap((userId) => getAbTest(userId!, abTestWebservice, AbTestCampaignNameEnum.NewUserJourney)),
      map((newUserJourneyAbTestVariant) =>
        initABTestCampaignsSuccess({
          abtestValues: {
            [AbTestCampaignNameEnum.NewUserJourney]: newUserJourneyAbTestVariant,
          },
        })
      )
    ),
  { functional: true }
);

const getAbTestCampaignByName = (name: AbTestCampaignNameEnum): { name: string; campaignId: string } | undefined => {
  const abtestConfig = environment.abTest[environment.i18n.locale] as
    | { name: string; campaignId: string }[]
    | undefined;
  return abtestConfig?.find((abtest) => abtest.name === name.toString());
};

const getAbTest = (
  userId: string,
  abTestWebservice: AbTestWebservice,
  abTestCampaignName: AbTestCampaignNameEnum
): Observable<{ value: string }> => {
  const abTest = getAbTestCampaignByName(abTestCampaignName);

  if (!abTest) {
    return of({ value: abTestDefaultVariantMapping[abTestCampaignName] });
  }

  return abTestWebservice.getVersionV2(abTest.campaignId, environment.i18n.country, UserTypeEnum.Member, userId).pipe(
    catchError((err: HttpErrorResponse) => {
      const errorDetail =
        typeof err.error === 'string'
          ? err.error
          : 'detail' in err.error
            ? (err.error as { detail: string }).detail
            : 'no detail available';
      Bugsnag.notify(
        new Error(
          `Please fix the ${abTestCampaignName} ab test campaign following error: ${err.status} - ${errorDetail}. The default value will be used`
        ),
        (event) => {
          event.severity = 'info';
        }
      );

      return of({ value: abTestDefaultVariantMapping[abTestCampaignName] });
    })
  );
};
